// client/src/hooks/useRecursos.js - VERSÃO 100% COMPLETA COM SUPORTE A POOL
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import useAlert from './useAlert';

const RECURSOS_QUERY_KEY = 'recursos';
const RECURSOS_POOL_QUERY_KEY = 'recursos-pool';

// Contrato canônico do payload — ver bloco de documentação em
// server/controllers/recursosController.js (CONTRATO CANÔNICO —
// RecursoPayload). metadata é subobjeto: o backend faz merge raso com o
// valor atual, mas prefira sempre enviar o objeto completo.

// ✅ FUNÇÃO AUXILIAR: Sanitizar parâmetros
const sanitizeRecursosParams = (params = {}) => {
  const sanitized = {};
  
  Object.keys(params).forEach(key => {
    const value = params[key];
    
    if (value !== undefined && value !== null && value !== '' && value !== 'todos') {
      if (typeof value === 'boolean') {
        sanitized[key] = value.toString();
      } else {
        sanitized[key] = value;
      }
    }
  });
  
  return sanitized;
};

// ✅ FUNÇÃO AUXILIAR: Validar Account ID
const validateAccountId = (accountId, hookName = '') => {
  if (!accountId || typeof accountId !== 'string' || accountId.length !== 24) {
    console.warn(`⚠️ [${hookName}] Account ID inválido ou não fornecido, ignorando inserção manual`);
    return null;
  }
  return accountId;
};

// ✅ FUNÇÃO AUXILIAR: Validar Resource ID
const validateResourceId = (id, context = '') => {
  if (!id) {
    const errorMsg = `❌ [VALIDATE_RESOURCE_ID] ID não fornecido em ${context}`;
    console.error(errorMsg);
    throw new Error(errorMsg);
  }
  
  const idStr = id.toString ? id.toString() : String(id);
  
  if (!idStr.match(/^[0-9a-fA-F]{24}$/)) {
    const errorMsg = `❌ [VALIDATE_RESOURCE_ID] ID inválido em ${context}: ${idStr}`;
    console.error(errorMsg);
    throw new Error(errorMsg);
  }
  
  console.log(`✅ [VALIDATE_RESOURCE_ID] ID válido em ${context}:`, idStr);
  return idStr;
};

// ✅ FUNÇÃO AUXILIAR: Extrair URL da imagem do recurso
const extrairImagemUrlDoRecurso = async (recurso) => {
  if (!recurso) return null;
  
  console.log('🖼️ [EXTRAIR_IMAGEM_URL] Processando recurso para imagem:', {
    id: recurso._id,
    temImagemUrl: !!recurso.imagemUrl,
    imagemPrincipal: recurso.imagemPrincipal,
    imagensCount: recurso.imagens?.length,
    imagensDaGaleriaCount: recurso.imagensDaGaleria?.length
  });
  
  if (recurso.imagemUrl && recurso.imagemUrl !== 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgdmlld0JveD0iMCAwIDE1MCAxNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxNTAiIGhlaWdodD0iMTUwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik03NSA0MEM2OC4zNjA0IDQwIDYyLjg3ODcgNDUuNDgxNyA2Mi44Nzg3IDUyLjEyNUM2Mi44Nzg3IDU4Ljc2ODMgNjguMzYwNCA2NC4yNSA3NSA0NC4yNUM4MS42Mzk2IDY0LjI1IDg3LjEyMTMgNTguNzY4MyA4Ny4xMjEzIDUyLjEyNUM4Ny4xMjEzIDQ1LjQ4MTcgODEuNjM5NiA0MCA3NSA0MFoiIGZpbGw9IiNBOUE5QTkiLz4KPHBhdGggZD0iTTUzLjEyNSAxMDAuNjI1QzUzLjEyNSA5My45ODE3IDU4LjYwNjcgODguNSA2NS4yNSA4OC41SDg0Ljc1QzkxLjM5MzMgODguNSA5Ni44NzUgOTMuOTgxNyA5Ni44NzUgMTAwLjYyNVYxMTIuNUM5Ni44NzUgMTE1LjgxMyA5NC4xODc1IDExOC41IDkwLjg3NSAxMTguNUg1OS4xMjVDNTUuODEyNSAxMTguNSA1My4xMjUgMTE1LjgxMyA1My4xMjUgMTEyLjVWMTAwLjYyNVoiIGZpbGw9IiNBOUE5QTkiLz4KPC9zdmc+') {
    return recurso.imagemUrl;
  }
  
  if (recurso.imagemPrincipal && recurso.imagemPrincipal.refId) {
    const principalRefId = recurso.imagemPrincipal.refId.toString();
    
    if (recurso.imagens && recurso.imagens.length > 0) {
      const imagemPrincipal = recurso.imagens.find(img => 
        img._id && img._id.toString() === principalRefId
      );
      if (imagemPrincipal && imagemPrincipal.arquivo) {
        console.log('🎯 [EXTRAIR_IMAGEM_URL] Imagem encontrada em imagens custom:', imagemPrincipal.arquivo);
        return imagemPrincipal.arquivo;
      }
    }
    
    if (recurso.imagensDaGaleria && recurso.imagensDaGaleria.length > 0) {
      const imagemPrincipal = recurso.imagensDaGaleria.find(img => 
        img._id && img._id.toString() === principalRefId
      );
      if (imagemPrincipal && imagemPrincipal.arquivo) {
        console.log('🎯 [EXTRAIR_IMAGEM_URL] Imagem encontrada em galeria:', imagemPrincipal.arquivo);
        return imagemPrincipal.arquivo;
      }
    }
  }
  
  if (recurso.imagens && recurso.imagens.length > 0) {
    const primeiraImagem = recurso.imagens[0];
    if (primeiraImagem && primeiraImagem.arquivo) {
      console.log('📷 [EXTRAIR_IMAGEM_URL] Usando primeira imagem custom:', primeiraImagem.arquivo);
      return primeiraImagem.arquivo;
    }
  }
  
  if (recurso.imagensDaGaleria && recurso.imagensDaGaleria.length > 0) {
    const primeiraImagem = recurso.imagensDaGaleria[0];
    if (primeiraImagem && primeiraImagem.arquivo) {
      console.log('📷 [EXTRAIR_IMAGEM_URL] Usando primeira imagem da galeria:', primeiraImagem.arquivo);
      return primeiraImagem.arquivo;
    }
  }
  
  console.log('❌ [EXTRAIR_IMAGEM_URL] Nenhuma imagem encontrada');
  return null;
};

// ✅ FUNÇÃO AUXILIAR: Processar resposta da API
const processRecursosResponse = (responseData, context = '') => {
  if (!responseData) {
    console.warn(`⚠️ [${context}] Resposta vazia da API`);
    return { success: false, data: [], count: 0, totalCount: 0, pagination: {} };
  }

  let finalData = [];
  let success = true;
  let count = 0;
  let totalCount = 0;

  if (responseData.success !== undefined && responseData.data !== undefined) {
    finalData = Array.isArray(responseData.data) 
      ? responseData.data.map(item => ({
          ...item,
          _id: item._id || item.id
        }))
      : [];
    success = responseData.success;
    count = responseData.count || finalData.length;
    totalCount = responseData.totalCount || count;
  }
  else if (Array.isArray(responseData)) {
    finalData = responseData.map(item => ({
      ...item,
      _id: item._id || item.id
    }));
    count = responseData.length;
    totalCount = responseData.length;
  }
  else if (responseData.data && Array.isArray(responseData.data)) {
    finalData = responseData.data.map(item => ({
      ...item,
      _id: item._id || item.id
    }));
    count = finalData.length;
    totalCount = responseData.totalCount || count;
  }
  else {
    finalData = [{
      ...responseData,
      _id: responseData._id || responseData.id
    }].filter(Boolean);
    count = finalData.length;
    totalCount = finalData.length;
  }

  console.log(`✅ [${context}] Dados processados:`, {
    success,
    dataLength: finalData.length,
    count,
    totalCount,
    hasIds: finalData.every(item => item._id)
  });

  return {
    success,
    data: finalData,
    count,
    totalCount,
    pagination: responseData.pagination || {}
  };
};

// ======================================================
// ✅ HOOKS DE RECURSOS (EXISTENTES)
// ======================================================

export const useRecursos = (accountIdOrParams, optionsParams = {}) => {
  const { setAlert } = useAlert();

  let accountId = null;
  let params = {};

  if (typeof accountIdOrParams === 'string' && accountIdOrParams.length > 0) {
    accountId = accountIdOrParams;
    params = optionsParams || {};
  } else if (typeof accountIdOrParams === 'object' && accountIdOrParams !== null) {
    params = accountIdOrParams;
  }

  return useQuery({
    queryKey: [RECURSOS_QUERY_KEY, accountId, params],
    queryFn: async () => {
      const cleanParams = sanitizeRecursosParams(params);
      delete cleanParams.account;

      console.log('🔍 [USE_RECURSOS] Buscando recursos:', cleanParams);

      try {
        const { data } = await api.get('/recursos', {
          params: cleanParams,
          timeout: 15000
        });
        
        console.log('✅ [USE_RECURSOS] Sucesso:', {
          success: data?.success,
          count: data?.count,
          dataLength: data?.data?.length
        });
        
        return processRecursosResponse(data, 'USE_RECURSOS');
        
      } catch (error) {
        console.error('❌ [USE_RECURSOS] Erro:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        });
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {
            page: parseInt(cleanParams.page) || 1,
            limit: parseInt(cleanParams.limit) || 10,
            totalPages: 0,
            totalCount: 0,
            hasNext: false,
            hasPrev: false
          },
          error: error.message
        };
      }
    },
    enabled: true,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1
  });
};

export const useBuscarRecursos = (accountId, filters = {}) => {
  const { setAlert } = useAlert();

  return useQuery({
    queryKey: [RECURSOS_QUERY_KEY, 'buscar', accountId, filters],
    queryFn: async () => {
      const cleanFilters = sanitizeRecursosParams(filters);
      delete cleanFilters.account;

      console.log('🔍 [USE_BUSCAR_RECURSOS] Buscando recursos com filtros:', cleanFilters);

      try {
        const { data } = await api.get('/recursos/buscar', {
          params: cleanFilters,
          timeout: 10000
        });
        
        return processRecursosResponse(data, 'USE_BUSCAR_RECURSOS');
      } catch (error) {
        console.error('❌ [USE_BUSCAR_RECURSOS] Erro:', error.message);
        
        if (error.response?.status !== 404) {
          setAlert(error.response?.data?.error || 'Erro ao buscar recursos', 'danger');
        }
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {},
          error: error.message
        };
      }
    },
    enabled: !!accountId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useRecursosPorUnidade = (unidadeId) => {
  const { setAlert } = useAlert();

  return useQuery({
    queryKey: [RECURSOS_QUERY_KEY, 'unidade', unidadeId],
    queryFn: async () => {
      if (!unidadeId) {
        console.warn('❌ [USE_RECURSOS_POR_UNIDADE] Unidade ID não fornecido');
        return { success: false, data: [], count: 0 };
      }

      console.log('🔍 [USE_RECURSOS_POR_UNIDADE] Buscando recursos da unidade:', unidadeId);

      try {
        const { data } = await api.get(`/recursos/unidade/${unidadeId}/recursos`, {
          timeout: 10000
        });
        
        return processRecursosResponse(data, 'USE_RECURSOS_POR_UNIDADE');
      } catch (error) {
        console.error('❌ [USE_RECURSOS_POR_UNIDADE] Erro:', error.message);
        
        if (error.response?.status !== 404) {
          setAlert(error.response?.data?.error || 'Erro ao carregar recursos da unidade', 'danger');
        }
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {},
          error: error.message
        };
      }
    },
    enabled: !!unidadeId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useHierarquiaUnidade = (unidadeId) => {
  const { setAlert } = useAlert();

  return useQuery({
    queryKey: [RECURSOS_QUERY_KEY, 'hierarquia', unidadeId],
    queryFn: async () => {
      if (!unidadeId) {
        console.warn('❌ [USE_HIERARQUIA_UNIDADE] Unidade ID não fornecido');
        return { success: false, data: [], count: 0 };
      }

      console.log('🔍 [USE_HIERARQUIA_UNIDADE] Buscando hierarquia da unidade:', unidadeId);

      try {
        const { data } = await api.get(`/recursos/unidade/${unidadeId}/hierarquia`, {
          timeout: 10000
        });
        
        return processRecursosResponse(data, 'USE_HIERARQUIA_UNIDADE');
      } catch (error) {
        console.error('❌ [USE_HIERARQUIA_UNIDADE] Erro:', error.message);
        
        if (error.response?.status !== 404) {
          setAlert(error.response?.data?.error || 'Erro ao carregar hierarquia', 'danger');
        }
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {},
          error: error.message
        };
      }
    },
    enabled: !!unidadeId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useSalasComMacas = (accountId, unidadeId) => {
  const { setAlert } = useAlert();

  return useQuery({
    queryKey: [RECURSOS_QUERY_KEY, 'salas-com-macas', accountId, unidadeId],
    queryFn: async () => {
      if (!accountId || !unidadeId) {
        console.warn('❌ [USE_SALAS_COM_MACAS] Account ID ou Unidade ID não fornecido');
        return { success: false, data: [], count: 0 };
      }

      console.log('🔍 [USE_SALAS_COM_MACAS] Buscando salas com macas:', { accountId, unidadeId });

      try {
        const { data } = await api.get(`/recursos/unidade/${unidadeId}/salas-com-macas`, {
          timeout: 10000
        });
        
        return processRecursosResponse(data, 'USE_SALAS_COM_MACAS');
      } catch (error) {
        console.error('❌ [USE_SALAS_COM_MACAS] Erro:', error.message);
        
        if (error.response?.status !== 404) {
          setAlert(error.response?.data?.error || 'Erro ao carregar salas', 'danger');
        }
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {},
          error: error.message
        };
      }
    },
    enabled: !!accountId && !!unidadeId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useEquipamentosMoveis = (accountId, unidadeId = null) => {
  const { setAlert } = useAlert();

  return useQuery({
    queryKey: [RECURSOS_QUERY_KEY, 'equipamentos-moveis', accountId, unidadeId],
    queryFn: async () => {
      console.log('🔍 [USE_EQUIPAMENTOS_MOVEIS] Buscando equipamentos móveis:', { accountId, unidadeId });

      try {
        const url = unidadeId 
          ? `/recursos/unidade/${unidadeId}/equipamentos-moveis`
          : `/recursos/equipamentos-moveis`;
        
        const params = {};
        const { data } = await api.get(url, {
          params,
          timeout: 10000
        });
        
        return processRecursosResponse(data, 'USE_EQUIPAMENTOS_MOVEIS');
      } catch (error) {
        console.error('❌ [USE_EQUIPAMENTOS_MOVEIS] Erro:', error.message);
        
        if (error.response?.status !== 404) {
          setAlert(error.response?.data?.error || 'Erro ao carregar equipamentos', 'danger');
        }
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {},
          error: error.message
        };
      }
    },
    enabled: true,
    staleTime: 2 * 60 * 1000,
  });
};

export const useRecursosEstatisticas = (accountId) => {
  const { setAlert } = useAlert();

  return useQuery({
    queryKey: [RECURSOS_QUERY_KEY, 'estatisticas', accountId],
    queryFn: async () => {
      console.log('📊 [USE_RECURSOS_ESTATISTICAS] Buscando estatísticas:', accountId);

      try {
        const params = {};
        const { data } = await api.get('/recursos/estatisticas', {
          params,
          timeout: 10000
        });
        
        return {
          success: true,
          data: data?.data || { porTipo: [], totais: {} },
          count: 0,
          totalCount: 0,
          pagination: {}
        };
      } catch (error) {
        console.error('❌ [USE_RECURSOS_ESTATISTICAS] Erro:', error.message);
        
        if (error.response?.status !== 404) {
          setAlert(error.response?.data?.error || 'Erro ao carregar estatísticas', 'danger');
        }
        
        return {
          success: false,
          data: { porTipo: [], totais: {} },
          count: 0,
          totalCount: 0,
          pagination: {},
          error: error.message
        };
      }
    },
    enabled: true,
    staleTime: 5 * 60 * 1000,
  });
};

export const useRecursosDisponiveisAgrupamento = (accountId, tipo = null, excluirId = null) => {
  const { setAlert } = useAlert();

  return useQuery({
    queryKey: [RECURSOS_QUERY_KEY, 'disponiveis-agrupamento', accountId, tipo, excluirId],
    queryFn: async () => {
      console.log('🔍 [USE_RECURSOS_DISPONIVEIS_AGRUPAMENTO] Buscando recursos para agrupamento:', {
        accountId, tipo, excluirId
      });

      try {
        const params = { tipo, excluirId };

        const { data } = await api.get('/recursos/disponiveis-agrupamento', {
          params,
          timeout: 10000
        });
        
        return processRecursosResponse(data, 'USE_RECURSOS_DISPONIVEIS_AGRUPAMENTO');
      } catch (error) {
        console.error('❌ [USE_RECURSOS_DISPONIVEIS_AGRUPAMENTO] Erro:', error.message);
        
        if (error.response?.status !== 404) {
          setAlert(error.response?.data?.error || 'Erro ao carregar recursos para agrupamento', 'danger');
        }
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {},
          error: error.message
        };
      }
    },
    enabled: true,
    staleTime: 2 * 60 * 1000,
  });
};

export const useRecursoStatus = (recursoId) => {
  const { setAlert } = useAlert();

  return useQuery({
    queryKey: [RECURSOS_QUERY_KEY, 'status', recursoId],
    queryFn: async () => {
      if (!recursoId) {
        console.warn('❌ [USE_RECURSO_STATUS] Recurso ID não fornecido');
        return { success: false, data: null };
      }

      console.log('🔍 [USE_RECURSO_STATUS] Buscando status do recurso:', recursoId);

      try {
        const { data } = await api.get(`/recursos/${recursoId}/status`, {
          timeout: 10000
        });
        
        return {
          success: true,
          data: data?.data || null,
          count: 0,
          totalCount: 0,
          pagination: {}
        };
      } catch (error) {
        console.error('❌ [USE_RECURSO_STATUS] Erro:', error.message);
        
        if (error.response?.status !== 404) {
          setAlert(error.response?.data?.error || 'Erro ao carregar status', 'danger');
        }
        
        return {
          success: false,
          data: null,
          count: 0,
          totalCount: 0,
          pagination: {},
          error: error.message
        };
      }
    },
    enabled: !!recursoId,
    staleTime: 1 * 60 * 1000,
  });
};

export const useRecursoUnidadesPermitidas = (recursoId) => {
  const { setAlert } = useAlert();

  return useQuery({
    queryKey: [RECURSOS_QUERY_KEY, 'unidades-permitidas', recursoId],
    queryFn: async () => {
      if (!recursoId) {
        console.warn('❌ [USE_RECURSO_UNIDADES_PERMITIDAS] Recurso ID não fornecido');
        return { success: false, data: null };
      }

      console.log('🔍 [USE_RECURSO_UNIDADES_PERMITIDAS] Buscando unidades permitidas:', recursoId);

      try {
        const { data } = await api.get(`/recursos/${recursoId}/unidades-permitidas`, {
          timeout: 10000
        });
        
        return {
          success: true,
          data: data?.data || null,
          count: 0,
          totalCount: 0,
          pagination: {}
        };
      } catch (error) {
        console.error('❌ [USE_RECURSO_UNIDADES_PERMITIDAS] Erro:', error.message);
        
        if (error.response?.status !== 404) {
          setAlert(error.response?.data?.error || 'Erro ao carregar unidades permitidas', 'danger');
        }
        
        return {
          success: false,
          data: null,
          count: 0,
          totalCount: 0,
          pagination: {},
          error: error.message
        };
      }
    },
    enabled: !!recursoId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useRecursoPaiDisponiveis = (accountId, tipoRecursoFilho, unidadeId = null) => {
  const { setAlert } = useAlert();

  return useQuery({
    queryKey: [RECURSOS_QUERY_KEY, 'recursos-pai-disponiveis', accountId, tipoRecursoFilho, unidadeId],
    queryFn: async () => {
      console.log('🔍 [USE_RECURSO_PAI_DISPONIVEIS] Buscando recursos pai:', {
        accountId, tipoRecursoFilho, unidadeId
      });

      try {
        const params = { tipoRecursoFilho, unidadeId };

        const { data } = await api.get('/recursos/recursos-pai-disponiveis', {
          params,
          timeout: 10000
        });
        
        return processRecursosResponse(data, 'USE_RECURSO_PAI_DISPONIVEIS');
      } catch (error) {
        console.error('❌ [USE_RECURSO_PAI_DISPONIVEIS] Erro:', error.message);
        
        if (error.response?.status !== 404) {
          setAlert(error.response?.data?.error || 'Erro ao carregar recursos pai', 'danger');
        }
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {},
          error: error.message
        };
      }
    },
    enabled: true,
    staleTime: 2 * 60 * 1000,
  });
};

export const useRecurso = (recursoId) => {
  const { setAlert } = useAlert();

  return useQuery({
    queryKey: [RECURSOS_QUERY_KEY, 'detalhes', recursoId],
    queryFn: async () => {
      if (!recursoId) {
        console.warn('❌ [USE_RECURSO] Recurso ID não fornecido');
        return { success: false, data: null };
      }

      console.log('🔍 [USE_RECURSO] Buscando detalhes do recurso:', recursoId);

      try {
        const { data } = await api.get(`/recursos/${recursoId}`, {
          timeout: 10000
        });
        
        const recursoData = data?.data || data;
        const recursoComId = recursoData ? {
          ...recursoData,
          _id: recursoData._id || recursoData.id || recursoId,
          configuracoesAgenda: recursoData.agendaDefinicoes?.configuracoes || recursoData.configuracoesAgenda || {}
        } : null;
        
        return {
          success: true,
          data: recursoComId,
          count: 0,
          totalCount: 0,
          pagination: {}
        };
      } catch (error) {
        console.error('❌ [USE_RECURSO] Erro:', error.message);
        
        if (error.response?.status !== 404) {
          setAlert(error.response?.data?.error || 'Erro ao carregar recurso', 'danger');
        }
        
        return {
          success: false,
          data: null,
          count: 0,
          totalCount: 0,
          pagination: {},
          error: error.message
        };
      }
    },
    enabled: !!recursoId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useRecursoHierarquia = (recursoId) => {
  const { setAlert } = useAlert();

  return useQuery({
    queryKey: [RECURSOS_QUERY_KEY, 'hierarquia-recurso', recursoId],
    queryFn: async () => {
      if (!recursoId) {
        console.warn('❌ [USE_RECURSO_HIERARQUIA] Recurso ID não fornecido');
        return { success: false, data: [] };
      }

      console.log('🔍 [USE_RECURSO_HIERARQUIA] Buscando hierarquia do recurso:', recursoId);

      try {
        const { data } = await api.get(`/recursos/${recursoId}/hierarquia`, {
          timeout: 10000
        });
        
        return processRecursosResponse(data, 'USE_RECURSO_HIERARQUIA');
      } catch (error) {
        console.error('❌ [USE_RECURSO_HIERARQUIA] Erro:', error.message);
        
        if (error.response?.status !== 404) {
          setAlert(error.response?.data?.error || 'Erro ao carregar hierarquia', 'danger');
        }
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {},
          error: error.message
        };
      }
    },
    enabled: !!recursoId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useRecursoFilhos = (recursoId) => {
  const { setAlert } = useAlert();

  return useQuery({
    queryKey: [RECURSOS_QUERY_KEY, 'filhos', recursoId],
    queryFn: async () => {
      if (!recursoId) {
        console.warn('❌ [USE_RECURSO_FILHOS] Recurso ID não fornecido');
        return { success: false, data: [] };
      }

      console.log('🔍 [USE_RECURSO_FILHOS] Buscando recursos filhos:', recursoId);

      try {
        const { data } = await api.get(`/recursos/${recursoId}/filhos`, {
          timeout: 10000
        });
        
        return processRecursosResponse(data, 'USE_RECURSO_FILHOS');
      } catch (error) {
        console.error('❌ [USE_RECURSO_FILHOS] Erro:', error.message);
        
        if (error.response?.status !== 404) {
          setAlert(error.response?.data?.error || 'Erro ao carregar recursos filhos', 'danger');
        }
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {},
          error: error.message
        };
      }
    },
    enabled: !!recursoId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useRecursosDependentes = (recursoId) => {
  const { setAlert } = useAlert();

  return useQuery({
    queryKey: [RECURSOS_QUERY_KEY, 'dependentes', recursoId],
    queryFn: async () => {
      if (!recursoId) {
        console.warn('❌ [USE_RECURSOS_DEPENDENTES] Recurso ID não fornecido');
        return { success: false, data: [] };
      }

      console.log('🔍 [USE_RECURSOS_DEPENDENTES] Buscando recursos dependentes:', recursoId);

      try {
        const { data } = await api.get(`/recursos/${recursoId}/dependentes`, {
          timeout: 10000
        });
        
        return processRecursosResponse(data, 'USE_RECURSOS_DEPENDENTES');
      } catch (error) {
        console.error('❌ [USE_RECURSOS_DEPENDENTES] Erro:', error.message);
        
        if (error.response?.status !== 404) {
          setAlert(error.response?.data?.error || 'Erro ao carregar dependências', 'danger');
        }
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {},
          error: error.message
        };
      }
    },
    enabled: !!recursoId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useResumoPrecificacao = (recursoId) => {
  const { setAlert } = useAlert();

  return useQuery({
    queryKey: [RECURSOS_QUERY_KEY, 'resumo-precificacao', recursoId],
    queryFn: async () => {
      if (!recursoId) {
        console.warn('❌ [USE_RESUMO_PRECIFICACAO] Recurso ID não fornecido');
        return { success: false, data: null };
      }

      console.log('🔍 [USE_RESUMO_PRECIFICACAO] Buscando resumo de precificação:', recursoId);

      try {
        const { data } = await api.get(`/recursos/${recursoId}/precificacao`, {
          timeout: 10000
        });
        
        return {
          success: true,
          data: data?.data || null,
          count: 0,
          totalCount: 0,
          pagination: {}
        };
      } catch (error) {
        console.error('❌ [USE_RESUMO_PRECIFICACAO] Erro:', error.message);
        
        if (error.response?.status !== 404) {
          setAlert(error.response?.data?.error || 'Erro ao carregar resumo de precificação', 'danger');
        }
        
        return {
          success: false,
          data: null,
          count: 0,
          totalCount: 0,
          pagination: {},
          error: error.message
        };
      }
    },
    enabled: !!recursoId,
    staleTime: 2 * 60 * 1000,
  });
};

// ======================================================
// ✅ NOVOS HOOKS: POOL DE RECURSOS
// ======================================================

export const useRecursosPool = (accountId, filters = {}) => {
  const { setAlert } = useAlert();

  return useQuery({
    queryKey: [RECURSOS_POOL_QUERY_KEY, 'list', accountId, filters],
    queryFn: async () => {
      const cleanFilters = sanitizeRecursosParams(filters);
      
      console.log('🔍 [USE_RECURSOS_POOL] Buscando pools de recursos:', cleanFilters);

      try {
        const { data } = await api.get('/recursos/pools', {
          params: cleanFilters,
          timeout: 10000
        });
        
        return processRecursosResponse(data, 'USE_RECURSOS_POOL');
      } catch (error) {
        console.error('❌ [USE_RECURSOS_POOL] Erro:', error.message);
        
        if (error.response?.status !== 404) {
          setAlert(error.response?.data?.error || 'Erro ao carregar pools', 'danger');
        }
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {},
          error: error.message
        };
      }
    },
    enabled: true,
    staleTime: 2 * 60 * 1000,
  });
};

export const useRecursoPool = (poolId) => {
  const { setAlert } = useAlert();

  return useQuery({
    queryKey: [RECURSOS_POOL_QUERY_KEY, 'detalhes', poolId],
    queryFn: async () => {
      if (!poolId) {
        console.warn('❌ [USE_RECURSO_POOL] Pool ID não fornecido');
        return { success: false, data: null };
      }

      console.log('🔍 [USE_RECURSO_POOL] Buscando detalhes do pool:', poolId);

      try {
        const { data } = await api.get(`/recursos/pools/${poolId}`, {
          timeout: 10000
        });
        
        const poolData = data?.data || data;
        const poolComId = poolData ? {
          ...poolData,
          _id: poolData._id || poolData.id || poolId
        } : null;
        
        return {
          success: true,
          data: poolComId,
          count: 0,
          totalCount: 0,
          pagination: {}
        };
      } catch (error) {
        console.error('❌ [USE_RECURSO_POOL] Erro:', error.message);
        
        if (error.response?.status !== 404) {
          setAlert(error.response?.data?.error || 'Erro ao carregar pool', 'danger');
        }
        
        return {
          success: false,
          data: null,
          count: 0,
          totalCount: 0,
          pagination: {},
          error: error.message
        };
      }
    },
    enabled: !!poolId,
    staleTime: 2 * 60 * 1000,
  });
};

export const usePoolsPorUnidade = (unidadeId) => {
  const { setAlert } = useAlert();

  return useQuery({
    queryKey: [RECURSOS_POOL_QUERY_KEY, 'unidade', unidadeId],
    queryFn: async () => {
      if (!unidadeId) {
        console.warn('❌ [USE_POOLS_POR_UNIDADE] Unidade ID não fornecido');
        return { success: false, data: [], count: 0 };
      }

      console.log('🔍 [USE_POOLS_POR_UNIDADE] Buscando pools da unidade:', unidadeId);

      try {
        const { data } = await api.get(`/recursos/unidade/${unidadeId}/pools`, {
          timeout: 10000
        });
        
        return processRecursosResponse(data, 'USE_POOLS_POR_UNIDADE');
      } catch (error) {
        console.error('❌ [USE_POOLS_POR_UNIDADE] Erro:', error.message);
        
        if (error.response?.status !== 404) {
          setAlert(error.response?.data?.error || 'Erro ao carregar pools da unidade', 'danger');
        }
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {},
          error: error.message
        };
      }
    },
    enabled: !!unidadeId,
    staleTime: 2 * 60 * 1000,
  });
};

export const usePoolRecursosDisponiveis = () => {
  const { setAlert } = useAlert();

  return useMutation({
    mutationFn: ({ poolId, dataHora, duracaoMinutos, profissionalId }) => {
      const validPoolId = validateResourceId(poolId, 'USE_POOL_RECURSOS_DISPONIVEIS');
      
      console.log('🔍 [USE_POOL_RECURSOS_DISPONIVEIS] Verificando disponibilidade:', {
        poolId: validPoolId,
        dataHora,
        duracaoMinutos,
        profissionalId
      });
      
      return api.post(`/recursos/pools/${validPoolId}/disponibilidade`, {
        dataHora,
        duracaoMinutos,
        profissionalId
      });
    },
    onError: (error) => {
      console.error('❌ [USE_POOL_RECURSOS_DISPONIVEIS] Erro:', error.message);
      setAlert(error.response?.data?.error || 'Erro ao verificar disponibilidade do pool', 'danger');
    }
  });
};

export const useAlocarRecursoPool = () => {
  const { setAlert } = useAlert();

  return useMutation({
    mutationFn: ({ poolId, dataHora, duracaoMinutos, profissionalId }) => {
      const validPoolId = validateResourceId(poolId, 'USE_ALOCAR_RECURSO_POOL');
      
      console.log('🎯 [USE_ALOCAR_RECURSO_POOL] Alocando recurso automaticamente:', {
        poolId: validPoolId,
        dataHora,
        duracaoMinutos,
        profissionalId
      });
      
      return api.post(`/recursos/pools/${validPoolId}/alocar`, {
        dataHora,
        duracaoMinutos,
        profissionalId
      });
    },
    onSuccess: (response) => {
      setAlert('Recurso alocado automaticamente com sucesso!', 'success');
      return response.data;
    },
    onError: (error) => {
      console.error('❌ [USE_ALOCAR_RECURSO_POOL] Erro:', error.message);
      setAlert(error.response?.data?.error || 'Erro ao alocar recurso do pool', 'danger');
    }
  });
};

export const useSincronizarPool = () => {
  const { setAlert } = useAlert();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (poolId) => {
      const validPoolId = validateResourceId(poolId, 'USE_SINCRONIZAR_POOL');
      
      console.log('🔄 [USE_SINCRONIZAR_POOL] Sincronizando pool:', validPoolId);
      
      return api.post(`/recursos/pools/${validPoolId}/sincronizar`);
    },
    onSuccess: (response, poolId) => {
      queryClient.invalidateQueries({ queryKey: [RECURSOS_POOL_QUERY_KEY, 'detalhes', poolId] });
      queryClient.invalidateQueries({ queryKey: [RECURSOS_POOL_QUERY_KEY, 'list'] });
      setAlert('Pool sincronizado com sucesso!', 'success');
      return response.data;
    },
    onError: (error) => {
      console.error('❌ [USE_SINCRONIZAR_POOL] Erro:', error.message);
      setAlert(error.response?.data?.error || 'Erro ao sincronizar pool', 'danger');
    }
  });
};

export const usePoolEstatisticas = (accountId) => {
  const { setAlert } = useAlert();

  return useQuery({
    queryKey: [RECURSOS_POOL_QUERY_KEY, 'estatisticas', accountId],
    queryFn: async () => {
      console.log('📊 [USE_POOL_ESTATISTICAS] Buscando estatísticas de pools:', accountId);

      try {
        const { data } = await api.get('/recursos/pools/estatisticas', {
          timeout: 10000
        });
        
        return {
          success: true,
          data: data?.data || [],
          count: 0,
          totalCount: 0,
          pagination: {}
        };
      } catch (error) {
        console.error('❌ [USE_POOL_ESTATISTICAS] Erro:', error.message);
        
        if (error.response?.status !== 404) {
          setAlert(error.response?.data?.error || 'Erro ao carregar estatísticas de pools', 'danger');
        }
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {},
          error: error.message
        };
      }
    },
    enabled: true,
    staleTime: 5 * 60 * 1000,
  });
};

// ======================================================
// ✅ MUTAÇÕES (INCLUINDO POOL)
// ======================================================

export const useMutateRecursos = (accountId) => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  const invalidateCache = () => {
    queryClient.invalidateQueries({ queryKey: [RECURSOS_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [RECURSOS_POOL_QUERY_KEY] });
    console.log('🔄 [USE_MUTATE_RECURSOS] Cache invalidado');
  };

  const handleError = (error, defaultMessage, context = '') => {
    console.error(`❌ [USE_MUTATE_RECURSOS] Erro em ${context}:`, {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data
    });
    
    const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         error.message || 
                         defaultMessage;
    
    setAlert(errorMessage, 'danger');
    throw new Error(errorMessage);
  };

  const handleSuccess = (message, context = '') => {
    invalidateCache();
    setAlert(message, 'success');
    console.log(`✅ [USE_MUTATE_RECURSOS] Sucesso em ${context}:`, message);
  };

  // ======================================================
  // MUTAÇÕES DE RECURSOS (EXISTENTES)
  // ======================================================

  const validatePrecificacao = useMutation({
    mutationFn: (precificacaoData) => {
      console.log('🔍 [USE_MUTATE_RECURSOS] Validando precificação:', precificacaoData);
      return api.post('/recursos/validar-precificacao', precificacaoData);
    },
    onSuccess: () => handleSuccess('Precificação válida!', 'VALIDATE_PRECIFICACAO'),
    onError: (error) => handleError(error, 'Erro na validação da precificação', 'VALIDATE_PRECIFICACAO'),
  });

  const addRecurso = useMutation({
    mutationFn: async (recursoData) => {
      let recursoPaiId = null;
      if (recursoData.recursoPai) {
        if (typeof recursoData.recursoPai === 'object') {
          console.log('🔄 [ADD_RECURSO] Convertendo objeto recursoPai para ID');
          recursoPaiId = recursoData.recursoPai._id || recursoData.recursoPai.id || null;
        } else {
          recursoPaiId = recursoData.recursoPai;
        }
      }
      
      const dadosSanitizados = {
        ...recursoData,
        recursoPai: recursoPaiId,
        unidadesPermitidas: recursoData.unidadesPermitidas || [],
        dependencias: recursoData.dependencias || [],
        tags: recursoData.tags || [],
        imagens: recursoData.imagens || [],
        imagensDaGaleria: recursoData.imagensDaGaleria || [],
        pacotesDePreco: recursoData.pacotesDePreco || [],
        metadata: {
          tipoItem: recursoData.metadata?.tipoItem || ['interno'],
          categoriaItem: recursoData.metadata?.categoriaItem || 'locacao',
          disponivelOnline: recursoData.metadata?.disponivelOnline || false,
          movel: recursoData.metadata?.movel || false,
          capacidade: recursoData.metadata?.capacidade || 1,
          caracteristicas: recursoData.metadata?.caracteristicas || [],
          ...recursoData.metadata
        },
        disponivelPara: recursoData.disponivelPara || ['INTERNO_SERVICOS'],
        status: recursoData.status || 'Ativo',
        ativo: recursoData.ativo !== undefined ? recursoData.ativo : true,
        visivelParaCliente: recursoData.visivelParaCliente !== undefined ? recursoData.visivelParaCliente : true,
        tipoDePrecificacao: recursoData.tipoDePrecificacao || 'Por Hora',
        valorPorHora: recursoData.valorPorHora || 0,
        temAgendaPropria: recursoData.temAgendaPropria || false
      };

      console.log('🆕 [USE_MUTATE_RECURSOS] Criando recurso:', {
        accountId,
        dados: {
          nome: dadosSanitizados.nome,
          tipo: dadosSanitizados.tipo,
          categoria: dadosSanitizados.categoria,
          unidade: dadosSanitizados.unidade
        }
      });
      
      const response = await api.post('/recursos', dadosSanitizados);
      
      if (response.data && response.data.data) {
        const recursoCriado = response.data.data;
        const recursoComImagens = {
          ...recursoCriado,
          imagens: Array.isArray(recursoCriado.imagens) ? recursoCriado.imagens : [],
          imagensDaGaleria: Array.isArray(recursoCriado.imagensDaGaleria) ? recursoCriado.imagensDaGaleria : [],
          imagemPrincipal: recursoCriado.imagemPrincipal || null,
          imagemUrl: recursoCriado.imagemUrl || await extrairImagemUrlDoRecurso(recursoCriado)
        };
        
        return {
          ...response,
          data: {
            ...response.data,
            data: recursoComImagens
          }
        };
      }
      
      return response;
    },
    onSuccess: (response) => {
      handleSuccess('Recurso criado com sucesso!', 'ADD_RECURSO');
      return response.data;
    },
    onError: (error) => handleError(error, 'Erro ao criar recurso', 'ADD_RECURSO'),
  });

  const updateRecurso = useMutation({
    mutationFn: async ({ id, recursoData }) => {
        console.log('🔧 [UPDATE_RECURSO_HOOK] Iniciando atualização:', {
            id,
            recursoData: {
                nome: recursoData.nome,
                tipo: recursoData.tipo,
                recursoPai: recursoData.recursoPai,
                imagemUrl: recursoData.imagemUrl
            }
        });

        const dadosParaEnviar = { ...recursoData };
        
        if (dadosParaEnviar.recursoPai && typeof dadosParaEnviar.recursoPai === 'object') {
            dadosParaEnviar.recursoPai = dadosParaEnviar.recursoPai._id || 
                                         dadosParaEnviar.recursoPai.id || 
                                         null;
        }

        dadosParaEnviar.imagemUrl = recursoData.imagemUrl || null;

        const response = await api.put(`/recursos/${id}`, dadosParaEnviar);
        
        return response.data;
    },
    onSuccess: (data, variables) => {
        queryClient.invalidateQueries({ queryKey: [RECURSOS_QUERY_KEY, variables.recursoData?.account] });
        queryClient.invalidateQueries({ queryKey: [RECURSOS_QUERY_KEY, 'detalhes', variables.id] });
        
        if (data.data) {
            queryClient.setQueryData([RECURSOS_QUERY_KEY, 'detalhes', variables.id], data.data);
        }
        
        setAlert('Recurso atualizado com sucesso!', 'success');
    },
    onError: (error, variables) => {
        console.error('❌ [UPDATE_RECURSO_HOOK] Erro:', {
            error: error.response?.data,
            variables
        });
        const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Erro ao atualizar recurso';
        setAlert(errorMessage, 'danger');
    }
  });

  const deleteRecurso = useMutation({
    mutationFn: (id) => {
      const validId = validateResourceId(id, 'DELETE_RECURSO');
      console.log('🗑️ [USE_MUTATE_RECURSOS] Deletando recurso:', validId);
      return api.delete(`/recursos/${validId}`);
    },
    onSuccess: (response) => {
      handleSuccess('Recurso removido com sucesso!', 'DELETE_RECURSO');
      return response.data;
    },
    onError: (error) => {
      if (error.message !== 'Ação cancelada') {
        handleError(error, 'Erro ao remover recurso', 'DELETE_RECURSO');
      }
    }
  });

  // ======================================================
  // NOVAS MUTAÇÕES: POOL DE RECURSOS
  // ======================================================

  const addPool = useMutation({
    mutationFn: async (poolData) => {
      console.log('🆕 [USE_MUTATE_RECURSOS] Criando pool de recursos:', {
        nome: poolData.nome,
        tipo: poolData.tipo,
        quantidade: poolData.quantidade
      });
      
      const response = await api.post('/recursos/pools', poolData);
      return response.data;
    },
    onSuccess: () => {
      handleSuccess('Pool de recursos criado com sucesso!', 'ADD_POOL');
    },
    onError: (error) => handleError(error, 'Erro ao criar pool', 'ADD_POOL'),
  });

  const updatePool = useMutation({
    mutationFn: async ({ id, poolData }) => {
      console.log('🔧 [USE_MUTATE_RECURSOS] Atualizando pool:', { id, poolData });
      const response = await api.put(`/recursos/pools/${id}`, poolData);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [RECURSOS_POOL_QUERY_KEY, 'detalhes', variables.id] });
      queryClient.invalidateQueries({ queryKey: [RECURSOS_POOL_QUERY_KEY, 'list'] });
      setAlert('Pool atualizado com sucesso!', 'success');
    },
    onError: (error) => handleError(error, 'Erro ao atualizar pool', 'UPDATE_POOL'),
  });

  const deletePool = useMutation({
    mutationFn: (id) => {
      const validId = validateResourceId(id, 'DELETE_POOL');
      console.log('🗑️ [USE_MUTATE_RECURSOS] Deletando pool:', validId);
      return api.delete(`/recursos/pools/${validId}`);
    },
    onSuccess: () => {
      handleSuccess('Pool removido com sucesso!', 'DELETE_POOL');
    },
    onError: (error) => handleError(error, 'Erro ao remover pool', 'DELETE_POOL'),
  });

  // Mutações existentes (mantidas)
  const updateDisponibilidade = useMutation({
    mutationFn: ({ id, disponibilidadeData }) => {
      const validId = validateResourceId(id, 'UPDATE_DISPONIBILIDADE');
      return api.patch(`/recursos/${validId}/disponibilidade`, disponibilidadeData);
    },
    onSuccess: (response) => {
      handleSuccess('Disponibilidade atualizada com sucesso!', 'UPDATE_DISPONIBILIDADE');
      return response.data;
    },
    onError: (error) => handleError(error, 'Erro ao atualizar disponibilidade', 'UPDATE_DISPONIBILIDADE'),
  });

  const updatePrecificacao = useMutation({
    mutationFn: ({ id, precificacaoData }) => {
      const validId = validateResourceId(id, 'UPDATE_PRECIFICACAO');
      return api.put(`/recursos/${validId}/precificacao`, precificacaoData);
    },
    onSuccess: (response) => {
      handleSuccess('Precificação atualizada com sucesso!', 'UPDATE_PRECIFICACAO');
      return response.data;
    },
    onError: (error) => handleError(error, 'Erro ao atualizar precificação', 'UPDATE_PRECIFICACAO'),
  });

  const updateRecursoMetadata = useMutation({
    mutationFn: ({ id, metadata }) => {
      const validId = validateResourceId(id, 'UPDATE_RECURSO_METADATA');
      return api.patch(`/recursos/${validId}/metadata`, { metadata });
    },
    onSuccess: (response) => {
      handleSuccess('Metadados atualizados com sucesso!', 'UPDATE_RECURSO_METADATA');
      return response.data;
    },
    onError: (error) => handleError(error, 'Erro ao atualizar metadados', 'UPDATE_RECURSO_METADATA'),
  });

  const updateRecursoDependencias = useMutation({
    mutationFn: ({ id, dependencias, action = 'add' }) => {
      const validId = validateResourceId(id, 'UPDATE_RECURSO_DEPENDENCIAS');
      return api.patch(`/recursos/${validId}/dependencias`, { dependencias, action });
    },
    onSuccess: (response) => {
      handleSuccess('Dependências atualizadas com sucesso!', 'UPDATE_RECURSO_DEPENDENCIAS');
      return response.data;
    },
    onError: (error) => handleError(error, 'Erro ao atualizar dependências', 'UPDATE_RECURSO_DEPENDENCIAS'),
  });

  const updateUnidadesPermitidas = useMutation({
    mutationFn: ({ id, unidadesPermitidas }) => {
      const validId = validateResourceId(id, 'UPDATE_UNIDADES_PERMITIDAS');
      return api.put(`/recursos/${validId}/unidades-permitidas`, { unidadesPermitidas });
    },
    onSuccess: (response) => {
      handleSuccess('Unidades permitidas atualizadas com sucesso!', 'UPDATE_UNIDADES_PERMITIDAS');
      return response.data;
    },
    onError: (error) => handleError(error, 'Erro ao atualizar unidades permitidas', 'UPDATE_UNIDADES_PERMITIDAS'),
  });

  const updateRecursoStatus = useMutation({
    mutationFn: ({ id, status }) => {
      const validId = validateResourceId(id, 'UPDATE_RECURSO_STATUS');
      return api.patch(`/recursos/${validId}/status`, { status });
    },
    onSuccess: (response) => {
      const newStatus = response.data?.status || 'Status atualizado';
      handleSuccess(`Status alterado para ${newStatus}`, 'UPDATE_RECURSO_STATUS');
      return response.data;
    },
    onError: (error) => handleError(error, 'Erro ao atualizar status', 'UPDATE_RECURSO_STATUS'),
  });

  const updatePoolStatus = useMutation({
    mutationFn: ({ id, status }) => {
      const validId = validateResourceId(id, 'UPDATE_POOL_STATUS');
      return api.patch(`/recursos/pools/${validId}/status`, { status });
    },
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: [RECURSOS_POOL_QUERY_KEY, 'detalhes', variables.id] });
      handleSuccess(`Status do pool alterado para ${variables.status}`, 'UPDATE_POOL_STATUS');
      return response.data;
    },
    onError: (error) => handleError(error, 'Erro ao atualizar status do pool', 'UPDATE_POOL_STATUS'),
  });

  // Mutações de imagem (existentes)
  const uploadRecursoImagemPrincipal = useMutation({
    mutationFn: ({ recursoId, file }) => {
      const validId = validateResourceId(recursoId, 'UPLOAD_RECURSO_IMAGEM_PRINCIPAL');
      const formData = new FormData();
      formData.append('imagem', file);
      return api.put(`/recursos/${validId}/imagens/principal`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: (response) => {
      handleSuccess('Imagem principal atualizada com sucesso!', 'UPLOAD_RECURSO_IMAGEM_PRINCIPAL');
      return response.data;
    },
    onError: (error) => handleError(error, 'Erro ao fazer upload da imagem', 'UPLOAD_RECURSO_IMAGEM_PRINCIPAL'),
  });

  const uploadRecursoImagem = useMutation({
    mutationFn: ({ recursoId, file }) => {
      const validId = validateResourceId(recursoId, 'UPLOAD_RECURSO_IMAGEM');
      const formData = new FormData();
      formData.append('imagem', file);
      return api.post(`/recursos/${validId}/imagens`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: (response) => {
      handleSuccess('Imagem adicionada à galeria com sucesso!', 'UPLOAD_RECURSO_IMAGEM');
      return response.data;
    },
    onError: (error) => handleError(error, 'Erro ao adicionar imagem à galeria', 'UPLOAD_RECURSO_IMAGEM'),
  });

  const setRecursoImagemPrincipal = useMutation({
    mutationFn: ({ recursoId, tipo, refId }) => {
      const validRecursoId = validateResourceId(recursoId, 'SET_RECURSO_IMAGEM_PRINCIPAL');
      const validRefId = validateResourceId(refId, 'SET_RECURSO_IMAGEM_PRINCIPAL_REF');
      return api.patch(`/recursos/${validRecursoId}/imagens/principal`, { tipo, refId: validRefId });
    },
    onSuccess: (response) => {
      handleSuccess('Imagem principal definida com sucesso!', 'SET_RECURSO_IMAGEM_PRINCIPAL');
      return response.data;
    },
    onError: (error) => handleError(error, 'Erro ao definir imagem principal', 'SET_RECURSO_IMAGEM_PRINCIPAL'),
  });

  const removeRecursoImagem = useMutation({
    mutationFn: ({ recursoId, imagemId }) => {
      const validRecursoId = validateResourceId(recursoId, 'REMOVE_RECURSO_IMAGEM');
      const validImagemId = validateResourceId(imagemId, 'REMOVE_RECURSO_IMAGEM_IMAGE_ID');
      return api.delete(`/recursos/${validRecursoId}/imagens/${validImagemId}`);
    },
    onSuccess: (response) => {
      handleSuccess('Imagem removida com sucesso!', 'REMOVE_RECURSO_IMAGEM');
      return response.data;
    },
    onError: (error) => handleError(error, 'Erro ao remover imagem', 'REMOVE_RECURSO_IMAGEM'),
  });

  // Estados de loading
  const isLoading = {
    validatePrecificacao: validatePrecificacao.isPending,
    addRecurso: addRecurso.isPending,
    updateRecurso: updateRecurso.isPending,
    deleteRecurso: deleteRecurso.isPending,
    addPool: addPool.isPending,
    updatePool: updatePool.isPending,
    deletePool: deletePool.isPending,
    updateDisponibilidade: updateDisponibilidade.isPending,
    updatePrecificacao: updatePrecificacao.isPending,
    updateRecursoMetadata: updateRecursoMetadata.isPending,
    updateRecursoDependencias: updateRecursoDependencias.isPending,
    updateUnidadesPermitidas: updateUnidadesPermitidas.isPending,
    updateRecursoStatus: updateRecursoStatus.isPending,
    updatePoolStatus: updatePoolStatus.isPending,
    uploadRecursoImagemPrincipal: uploadRecursoImagemPrincipal.isPending,
    uploadRecursoImagem: uploadRecursoImagem.isPending,
    setRecursoImagemPrincipal: setRecursoImagemPrincipal.isPending,
    removeRecursoImagem: removeRecursoImagem.isPending
  };

  const isPending = Object.values(isLoading).some(status => status);

  return {
    // Mutações de recursos
    validatePrecificacao,
    addRecurso,
    updateRecurso,
    deleteRecurso,
    updateDisponibilidade,
    updatePrecificacao,
    updateRecursoMetadata,
    updateRecursoDependencias,
    updateUnidadesPermitidas,
    updateRecursoStatus,
    
    // Mutações de pool
    addPool,
    updatePool,
    deletePool,
    updatePoolStatus,
    
    // Mutações de imagem
    uploadRecursoImagemPrincipal,
    uploadRecursoImagem,
    setRecursoImagemPrincipal,
    removeRecursoImagem,
    
    // Utilitários
    isPending,
    isLoading,
    invalidateCache,
    forceRefetch: () => invalidateCache()
  };
};

export default useRecursos;